import { DEAD_BRANCH, type WorldData, type SimState, type MoveInput, type Vec3 } from './types';
export { DEAD_BRANCH };
interface Wasm {
    HEAPF32: Float32Array;
    _cp_clear(): void;
    _cp_version(): number;
    _cp_object(...args: number[]): void;
    _cp_spawn(...args: number[]): void;
    _cp_step(...args: number[]): void;
    _cp_read(id: number): number;
    _cp_write(id: number, ptr: number): void;
    _cp_clone(a: number, b: number): void;
    _cp_tick(): void;
    _cp_break_mode(id: number, mode: number): void;
    _cp_breaks_read(): number;
    _cp_breaks_write(ptr: number): number;
    _malloc(n: number): number;
}
/** Dead-branch timing in 120 Hz ticks (sim/canopy.hpp): crack warning, snap, regrow. */
export const CRACK_TICKS = 48, SNAP_TICKS = 84, REGROW_TICKS = 960;
/** Load on a dead branch carried by a monkey snapshot (slot 17 bits 13-19), in ticks. */
export const strainTicks = (raw: ArrayLike<number>) => (raw[17] >>> 13) & 127;
/**
 * A dead branch that is broken or straining. `down`/`strain` are raw ticks (wire format);
 * `broken` hides the branch, `regrow` is seconds until it returns, `crack` (0-1) is the warning
 * from the first visible crack to the snap and `load` (0-1) the strain toward the snap.
 */
export type BranchBreak = { id: number; down: number; strain: number; broken: boolean; regrow: number; crack: number; load: number };
/** How a sim slot uses the shared dead-branch state. */
export const BreakMode = { Isolated: 0, ReadOnly: 1, Live: 2 } as const;
export type BreakMode = typeof BreakMode[keyof typeof BreakMode];
const MAX_BREAKS = 1024;
export class Simulation {
    private scratch = 0;
    private breakScratch = 0;
    constructor(private module: Wasm) { this.scratch = module._malloc(160); if (!this.scratch) throw Error('The simulation could not reserve memory.'); }
    static async create() { const url = '/sim/canopy.js'; const factory = (await import(/* @vite-ignore */ url)).default; return new Simulation(await factory({ locateFile: (name: string) => `/sim/${name}` })); }
    get version() { return this.module._cp_version(); }
    load(world: WorldData) {
        this.module._cp_clear();
        for (const o of world.objects)
            this.module._cp_object(o.id, o.type, ...o.a, ...o.b, o.radius);
    }
    /** Advances the shared world (dead-branch regrowth, crack decay) one 120 Hz tick; call once per local step. */
    tick() { this.module._cp_tick(); }
    /** Slot 0 is Live by default (its snaps break branches); every other slot is ReadOnly. */
    breakMode(id: number, mode: BreakMode) { this.module._cp_break_mode(id, mode); }
    /** Every broken or straining dead branch, keyed by object id. Empty when the jungle is whole. */
    breaks(): Map<number, BranchBreak> {
        const flat = this.breaksRaw(), map = new Map<number, BranchBreak>();
        for (let i = 0; i < flat.length; i += 3) {
            const [id, down, strain] = [flat[i], flat[i + 1], flat[i + 2]];
            map.set(id, { id, down, strain, broken: down > 0, regrow: down / 120, crack: down > 0 ? 0 : Math.max(0, Math.min(1, (strain - CRACK_TICKS) / (SNAP_TICKS - CRACK_TICKS))), load: down > 0 ? 0 : Math.min(1, strain / SNAP_TICKS) });
        }
        return map;
    }
    /** Flat [id, downTicks, strainTicks, ...] wire form of breaks(). */
    breaksRaw(): number[] {
        const ptr = this.module._cp_breaks_read() / 4, heap = this.module.HEAPF32, n = Math.min(MAX_BREAKS, heap[ptr]);
        return Array.from(heap.subarray(ptr + 1, ptr + 1 + 3 * n));
    }
    /** Replaces the shared dead-branch state (room reconciliation, replays). */
    writeBreaks(flat: ArrayLike<number>) {
        const n = Math.min(MAX_BREAKS, Math.floor(flat.length / 3));
        if (!this.breakScratch && !(this.breakScratch = this.module._malloc(4 * (1 + 3 * MAX_BREAKS))))
            throw Error('The simulation could not reserve memory.');
        const heap = this.module.HEAPF32, base = this.breakScratch / 4;
        heap[base] = n;
        for (let i = 0; i < 3 * n; i++)
            heap[base + 1 + i] = flat[i];
        return this.module._cp_breaks_write(this.breakScratch) === 1;
    }
    spawn(id: number, p: Vec3) { this.module._cp_spawn(id, ...p); }
    step(id: number, input: MoveInput) { this.module._cp_step(id, input.x, input.z, input.buttons, input.assist); }
    read(id = 0): SimState { const ptr = this.module._cp_read(id) / 4; const a = Array.from(this.module.HEAPF32.subarray(ptr, ptr + 40)); return { p: a.slice(0, 3) as Vec3, v: a.slice(3, 6) as Vec3, anchor: a.slice(6, 9) as Vec3, radius: a[9], time: a[10], mode: a[15], grab: a[16], candidate: a[18], grabs: a[19], tails: a[20], falls: a[21], chain: a[22], bestChain: a[23], score: a[24], flex: a[25], autoTails: a[33], missReason: a[34], occluded: a[35], support: a[36], gripT: a[37], strain: strainTicks(a) / SNAP_TICKS, raw: a }; }
    restore(state: number[], id = 0) { this.module.HEAPF32.set(state, this.scratch / 4); this.module._cp_write(id, this.scratch); }
    predict(input: MoveInput, seconds = 2) {
        this.module._cp_clone(0, 30);
        const points: Vec3[] = [];
        for (let i = 0; i < seconds * 120; i++) {
            this.step(30, input);
            if (i % 6 === 0)
                points.push(this.read(30).p);
        }
        return points;
    }
}
